import { useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/utils/toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { getApiUrl } from "@/utils/api";
import { AsyncSelect } from "@/components/AsyncSelect";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

interface Comercializadora {
  id: number;
  name: string;
}

interface Comercializadora {
  id: number;
  name: string;
}

interface ElectricityFactor {
  id: number;
  year: number;
  comercializadora: number;
  coefficient: number;
}

const electricityFactorSchema = z.object({
  year: z.coerce.number().min(1900).max(2100),
  comercializadora: z.number(),
  coefficient: z.coerce.number().min(0),
});

type ElectricityFactorFormValues = z.infer<typeof electricityFactorSchema>;

interface ElectricityFactorFormProps {
  id?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function ElectricityFactorForm({
  id,
  onSuccess,
  onCancel,
}: ElectricityFactorFormProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  const form = useForm<ElectricityFactorFormValues>({
    resolver: zodResolver(electricityFactorSchema),
    defaultValues: {
      year: new Date().getFullYear(),
      comercializadora: null,
      coefficient: 0,
    },
  });

  // Fetch electricity factor data if in edit mode
  const { data: electricityFactorData, isLoading } = useQuery<ElectricityFactor & {
    comercializadora: Comercializadora; // Ensure comercializadora is properly typed
  }>({
      queryKey: ["electricity-factors", id],
      queryFn: async () => {
        const response = await fetch(getApiUrl(`electricity-factors/${id}/`), {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(
            t("coefficients.electricity.error.fetchElectricityFactor"),
          );
        }
        return response.json();
      },
      enabled: isEditMode,
    });

  // Fetcher function for comercializadoras (used by AsyncSelect)
  const fetchComercializadoras = useCallback(
    async (query?: string): Promise<Comercializadora[]> => {
      try {
        // If query is a number, it's an ID lookup for initialization
        if (query && /^\d+$/.test(query)) {
          // If we have the data already, use it
          if (
            electricityFactorData?.comercializadora &&
            electricityFactorData.comercializadora.id.toString() === query
          ) {
            return [electricityFactorData.comercializadora];
          }

          // Otherwise, fetch the specific comercializadora
          const response = await fetch(
            getApiUrl(`comercializadoras/${query}/`),
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (!response.ok) {
            throw new Error("Failed to fetch comercializadora");
          }
          return [await response.json()];
        }

        // Search by name for the dropdown - use the query parameter format that the API expects
        const base = getApiUrl('comercializadoras/');
        // In production getApiUrl returns a relative path (e.g. "/api/") which
        // makes new URL() throw without a base. Provide window.location.origin as base.
        const url = new URL(base, window.location.origin);
        if (query) {
          url.searchParams.append('name', query);
        } else {
          // If no query, still fetch a default list (first page)
          url.searchParams.append('limit', '20');
        }
        
        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        
        if (!response.ok) {
          throw new Error("Failed to fetch comercializadoras");
        }
        const data = await response.json();
        return Array.isArray(data) ? data : data.results || [];
      } catch (error) {
        console.error("Error fetching comercializadoras:", error);
        return [];
      }
    },
    [token, electricityFactorData]
  );

  // Prefetch comercializadora data when editing
  useQuery({
    queryKey: ["comercializadora", electricityFactorData?.comercializadora],
    queryFn: async () => {
      if (!electricityFactorData?.comercializadora) return undefined;
      return fetchComercializadoras(
        electricityFactorData.comercializadora.toString()
      );
    },
    enabled: isEditMode && !!electricityFactorData?.comercializadora,
    refetchOnWindowFocus: false,
  });

  // Set form values when data is loaded
  useEffect(() => {
    if (electricityFactorData) {
      form.reset({
        year: electricityFactorData.year,
        comercializadora: electricityFactorData.comercializadora.id,
        coefficient: electricityFactorData.coefficient,
      });
    }
  }, [electricityFactorData, form, isEditMode]);

  const mutation = useMutation({
    mutationFn: async (values: ElectricityFactorFormValues) => {
      const url = id
        ? getApiUrl(`electricity-factors/${id}/`)
        : getApiUrl("electricity-factors/");
      const method = id ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...values,
          comercializadora: values.comercializadora,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save electricity factor");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["electricity-factors"] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: ElectricityFactorFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading && isEditMode) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full"
      >
        <div className="space-y-6 flex-1">
          <FormField
            control={form.control}
            name="year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("coefficients.electricity.form.year")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1900}
                    max={2100}
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="comercializadora"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {t("coefficients.electricity.form.comercializadora")}
                    </label>
                    <div key={`comercializadora-${field.value || 'new'}`}>
                      <AsyncSelect<Comercializadora>
                        fetcher={fetchComercializadoras}
                        value={field.value?.toString() || ""}
                        onChange={(value) => {
                          field.onChange(value ? parseInt(value) : null);
                        }}
                        placeholder={t(
                          "coefficients.electricity.form.selectComercializadora"
                        )}
                        getOptionValue={(option) => option.id.toString()}
                        getDisplayValue={(option) => {
                          if (!option) return "";
                          if (typeof option === 'string') return option;
                          return option.name;
                        }}
                        renderOption={(option) => (
                          <div className="flex items-center">
                            <span>{option.name}</span>
                          </div>
                        )}
                        notFound={
                          <div className="py-6 text-center text-sm">
                            {t("common.no_results_found")}
                          </div>
                        }
                        noResultsMessage={t("common.no_results_found")}
                        width="100%"
                        label=""
                      />
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="coefficient"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("coefficients.electricity.form.coefficient")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end space-x-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? t("common.saving")
              : isEditMode
                ? t("common.update")
                : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
